// Chemin: C:\gestion-copro\src\components\documents\DocumentCategoryAccordion.tsx
// Session 64 : Composant accordéon catégorie pour la vue GED par catégorie
// Session 106 : Ajout prop basePath pour réutilisation VIGIE (principe ESCALATOR)
// Session 125 : Ajout prop excludeCategories pour masquer "Interne CS" côté VIGIE
// Session 233 : Ajout props onArchive + archivageEnCours — bouton Archive dans vue catégorie (VIGIE)
// Session 256 : Sous-groupement visuel par TYPE dans chaque catégorie
// Session 272 : Badge statutCourrier inline — brouillon/finalise/envoye
// Session 274 : Refonte cosmétique — icônes Lucide par catégorie, ordre alpha, charte graphique
// Session 282 : Badge statut paiement inline (Réglé/En attente/Partiel) + montant + date règlement
//               Header catégorie : bg-blue-900 (bleu sidebar VIGIE) — une seule couleur, sobre
//               Sous-groupe type : bg-blue-50, texte blue-800 — distinct sans agressivité

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Archive,
  ArchiveRestore,
  FileText,
  Shield,
  Calculator,
  AlertTriangle,
  Lock,
  Scale,
  Home,
  Building,
  Users,
  Wrench,
  HardHat,
  PackageOpen,
  LucideIcon,
  CheckCircle2,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { CategorieDocument } from '../../types/document.types';
import { CATEGORIES_DOCUMENT_OPTIONS } from '../../constants/documentTaxonomy';
import { getTypeDocumentLabel } from '../../services/document.service';

// ============================================
// TYPES
// ============================================

interface DocumentListItem {
  id: string;
  titre: string;
  type: string;
  categorie?: CategorieDocument;
  dateDocument: any;
  fichierType: string;
  visibilite?: 'cs_only' | 'public' | 'proprietaire';
  affaireId?: string;
  affaireTitre?: string;
  estArchive?: boolean;
  statutCourrier?: 'brouillon' | 'finalise' | 'envoye';
  dateEnvoiCourrier?: any;
  sens?: 'envoye' | 'recu';
  sensCorrespondance?: 'entrant' | 'sortant'; // Session 297 : fallback collection courriers
  donneesCles?: Record<string, string>; // Session 282 : statut paiement, montant, date règlement
}

interface DocumentCategoryAccordionProps {
  documents: DocumentListItem[];
  defaultOpenCategories?: CategorieDocument[];
  basePath?: string;
  excludeCategories?: CategorieDocument[];
  onArchive?: (doc: { id: string; estArchive?: boolean }) => void;
  archivageEnCours?: string | null;
}

// ============================================
// ICÔNES LUCIDE PAR CATÉGORIE
// Seule différenciation visuelle entre catégories
// La couleur est unique (blue-700) — charte graphique
// ============================================

const CATEGORIE_ICONS: Record<string, LucideIcon> = {
  administratif: FileText,
  assurance:     Shield,
  comptabilite:  Calculator,
  gpa:           AlertTriangle,
  interne_cs:    Lock,
  juridique:     Scale,
  mon_bien:      Home,
  promoteur:     Building,
  syndic:        Users,
  technique:     Wrench,
  travaux:       HardHat,
  non_classe:    PackageOpen,
};

const getCategorieIcon = (cat: CategorieDocument): LucideIcon =>
  CATEGORIE_ICONS[cat] || FileText;

// ============================================
// UTILITAIRES
// ============================================

const getFileIcon = (fichierType: string) => {
  if (fichierType?.includes('pdf')) {
    return <FileText className="w-5 h-5 text-red-400 shrink-0" />;
  }
  if (fichierType?.includes('image') || fichierType?.includes('jpg') || fichierType?.includes('png')) {
    return (
      <svg className="w-5 h-5 text-green-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
      </svg>
    );
  }
  return <FileText className="w-5 h-5 text-gray-400 shrink-0" />;
};

const formatDate = (timestamp: any): string => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};


const SensBadge = ({ sens, sensCorrespondance, statut, dateEnvoi, dateDocument }: {
  sens?: string;
  sensCorrespondance?: string; // Session 297 : fallback collection courriers ('entrant'|'sortant')
  statut?: string;
  dateEnvoi?: any;
  dateDocument?: any;
}) => {
  const formatDateCourte = (ts: any): string => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  };

  // Résolution unifiée : sens (nouveau) OU sensCorrespondance (ancien champ collection courriers)
  const sensResolu = sens
    ?? (sensCorrespondance === 'entrant' ? 'recu' : sensCorrespondance === 'sortant' ? 'envoye' : undefined);

  // Brouillon / Prêt à envoyer — priorité absolue
  if (statut === 'brouillon') {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-500 shrink-0">
        Brouillon
      </span>
    );
  }
  if (statut === 'finalise') {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700 shrink-0">
        Prêt à envoyer
      </span>
    );
  }

  // Envoyé — sensResolu ou statutCourrier
  if (sensResolu === 'envoye' || statut === 'envoye') {
    const date = formatDateCourte(dateEnvoi || dateDocument);
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700 shrink-0">
        Envoyé{date ? ` le ${date}` : ''}
      </span>
    );
  }

  // Reçu — sensResolu
  if (sensResolu === 'recu') {
    const date = formatDateCourte(dateDocument);
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700 shrink-0">
        Reçu{date ? ` le ${date}` : ''}
      </span>
    );
  }

  return null;
};

// ============================================
// SESSION 282 : BADGE STATUT PAIEMENT
// Affiché uniquement si donneesCles.statutPaiement est présent
// Types concernés : appel_de_fonds, regularisation_charges, quittance
// ============================================

const TYPES_PAIEMENT = new Set([
  'appel_de_fonds',
  'appel_fonds',
  'regularisation_charges',
  'quittance',
  'appel_provisions',
]);

const StatutPaiementBadge = ({ donneesCles, type }: { donneesCles?: Record<string, string>; type: string }) => {
  if (!donneesCles) return null;
  const statut = donneesCles.statutPaiement;
  if (!statut) return null;
  // N'afficher que pour les types financiers
  const typeNormalise = type?.toLowerCase().replace(/[^a-z_]/g, '_') || '';
  const estTypeFinancier = TYPES_PAIEMENT.has(typeNormalise) ||
    typeNormalise.includes('appel') ||
    typeNormalise.includes('provision') ||
    typeNormalise.includes('regularisation') ||
    typeNormalise.includes('quittance');
  if (!estTypeFinancier) return null;

  const montant = donneesCles.montant || donneesCles.montantTotal || donneesCles.montantDu;
  const datePaiementRaw = donneesCles.datePaiement || donneesCles.dateReglement;
  const modePaiement = donneesCles.modePaiement || donneesCles.moyenPaiement;
  // Formater la date — peut être string ISO "2026-03-16" ou string FR "16/03/2026"
  const datePaiement = (() => {
    if (!datePaiementRaw) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePaiementRaw)) {
      const [y, m, d] = datePaiementRaw.split('-');
      return `${d}/${m}/${y}`;
    }
    return datePaiementRaw;
  })();

  const statutNorm = statut.toLowerCase();

  if (statutNorm === 'regle' || statutNorm === 'réglé' || statutNorm === 'paye' || statutNorm === 'payé' || statutNorm === 'oui') {
    return (
      <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700 shrink-0">
          <CheckCircle2 size={11} />
          Réglé
        </span>
        {montant && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 shrink-0">
            {montant} €
          </span>
        )}
        {datePaiement && (
          <span className="text-xs text-gray-400 shrink-0">
            {modePaiement ? `${datePaiement} — ${modePaiement}` : datePaiement}
          </span>
        )}
      </div>
    );
  }

  if (statutNorm === 'en_attente' || statutNorm === 'impaye' || statutNorm === 'impayé' || statutNorm === 'a_regler') {
    return (
      <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-orange-100 text-orange-700 shrink-0">
          <Clock size={11} />
          En attente
        </span>
        {montant && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 shrink-0">
            {montant} €
          </span>
        )}
      </div>
    );
  }

  if (statutNorm === 'partiel' || statutNorm === 'partiellement_regle') {
    return (
      <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700 shrink-0">
          <AlertCircle size={11} />
          Partiel
        </span>
        {montant && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 shrink-0">
            {montant} €
          </span>
        )}
      </div>
    );
  }

  return null;
};

// ============================================
// LIGNE DOCUMENT
// ============================================

const DocumentRow = ({
  doc,
  basePath,
  onArchive,
  archivageEnCours,
}: {
  doc: DocumentListItem;
  basePath: string;
  onArchive?: (doc: { id: string; estArchive?: boolean }) => void;
  archivageEnCours?: string | null;
}) => (
  <li className="flex items-center bg-white hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0">
    <Link
      to={`${basePath}/${doc.id}`}
      className="flex items-center gap-3 px-5 py-3 flex-1 min-w-0"
    >
      {/* Session 294 : icône courrier uniforme — rouge pour courrier, email et EML */}
      {['courrier', 'email', 'EML'].includes(doc.type)
        ? <FileText className="w-5 h-5 text-red-400 shrink-0" />
        : getFileIcon(doc.fichierType)}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-medium truncate ${doc.estArchive ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
            {doc.titre}
          </span>
          {doc.estArchive && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 shrink-0">
              Masqué SENTINELLE
            </span>
          )}
          <SensBadge sens={doc.sens} sensCorrespondance={doc.sensCorrespondance} statut={doc.statutCourrier} dateEnvoi={doc.dateEnvoiCourrier} dateDocument={doc.dateDocument} />
        </div>
        {/* Session 282 : badge paiement sur ligne séparée — pas de surcharge sur la première ligne */}
        <div className="flex items-center gap-1 mt-0.5">
          <StatutPaiementBadge donneesCles={doc.donneesCles} type={doc.type} />
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
          {formatDate(doc.dateDocument) && <span>{formatDate(doc.dateDocument)}</span>}
          {doc.affaireTitre && <span className="text-blue-500">• {doc.affaireTitre}</span>}
        </div>
      </div>
      <svg className="w-4 h-4 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </Link>
    {onArchive && (
      <button
        onClick={(e) => { e.preventDefault(); onArchive(doc); }}
        disabled={archivageEnCours === doc.id}
        className={`p-2 mr-3 rounded-lg transition-colors shrink-0 ${
          doc.estArchive
            ? 'text-amber-500 hover:text-amber-700 hover:bg-amber-50'
            : 'text-gray-300 hover:text-amber-500 hover:bg-amber-50'
        }`}
        title={doc.estArchive ? 'Désarchiver' : 'Masquer à SENTINELLE'}
      >
        {doc.estArchive ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
      </button>
    )}
  </li>
);

// ============================================
// SOUS-GROUPE TYPE — bg-blue-50, texte blue-800
// Clairement distinct des lignes blanches, sans agressivité
// ============================================

const TypeSubGroup = ({
  type,
  docs,
  basePath,
  onArchive,
  archivageEnCours,
}: {
  type: string;
  docs: DocumentListItem[];
  basePath: string;
  onArchive?: (doc: { id: string; estArchive?: boolean }) => void;
  archivageEnCours?: string | null;
}) => {
  const [open, setOpen] = useState(true);
  const label = getTypeDocumentLabel(type);

  return (
    <div className="border-b border-blue-100 last:border-b-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-2.5 bg-blue-50 hover:bg-blue-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <svg
            className={`w-3 h-3 text-blue-400 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-xs font-bold uppercase tracking-widest text-blue-700">{label}</span>
        </div>
        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-200 text-blue-700">
          {docs.length}
        </span>
      </button>
      {open && (
        <ul>
          {docs.map(doc => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              basePath={basePath}
              onArchive={onArchive}
              archivageEnCours={archivageEnCours}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

export default function DocumentCategoryAccordion({
  documents,
  defaultOpenCategories = [],
  basePath = '/admin/documents',
  excludeCategories = [],
  onArchive,
  archivageEnCours = null,
}: DocumentCategoryAccordionProps) {
  // Tri alphabétique
  const visibleCategories = (
    excludeCategories.length > 0
      ? CATEGORIES_DOCUMENT_OPTIONS.filter(cat => !excludeCategories.includes(cat.value))
      : CATEGORIES_DOCUMENT_OPTIONS
  ).slice().sort((a, b) => a.label.localeCompare(b.label, 'fr'));

  const [openCategories, setOpenCategories] = useState<Set<CategorieDocument>>(
    new Set(defaultOpenCategories)
  );

  const documentsByCategory = visibleCategories.reduce((acc, cat) => {
    acc[cat.value] = documents.filter(doc => doc.categorie === cat.value);
    return acc;
  }, {} as Record<string, DocumentListItem[]>);

  // Rattrapage : documents sans catégorie valide → groupe "Non classés" en bas
  const categoriesValides = new Set(visibleCategories.map(c => c.value));
  const documentsNonClasses = documents.filter(doc =>
    !doc.categorie || !categoriesValides.has(doc.categorie)
  );

  const toggleCategory = (category: CategorieDocument) => {
    setOpenCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const hasDocuments = documents.length > 0;
  const allOpen = visibleCategories.every(cat =>
    documentsByCategory[cat.value].length === 0 || openCategories.has(cat.value)
  );

  const toggleAll = () => {
    if (allOpen) {
      setOpenCategories(new Set());
    } else {
      const cats = visibleCategories
        .filter(cat => documentsByCategory[cat.value].length > 0)
        .map(cat => cat.value);
      setOpenCategories(new Set(cats));
    }
  };

  const groupByType = (docs: DocumentListItem[]): { type: string; docs: DocumentListItem[] }[] => {
    const map = new Map<string, DocumentListItem[]>();
    docs.forEach(doc => {
      const t = doc.type || 'autre';
      if (!map.has(t)) map.set(t, []);
      map.get(t)!.push(doc);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1].length - a[1].length)
      .map(([type, docs]) => ({ type, docs }));
  };

  return (
    <div className="space-y-2">
      {hasDocuments && (
        <div className="flex justify-end mb-2">
          <button
            onClick={toggleAll}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            {allOpen ? (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
                Tout replier
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                Tout déplier
              </>
            )}
          </button>
        </div>
      )}

      {visibleCategories.map(category => {
        const categoryDocs = documentsByCategory[category.value];
        const count = categoryDocs.length;
        const isOpen = openCategories.has(category.value);
        const isEmpty = count === 0;
        const typeGroups = groupByType(categoryDocs);
        const Icon = getCategorieIcon(category.value);

        return (
          <div
            key={category.value}
            className={`border border-blue-200 rounded-xl overflow-hidden shadow-sm ${isEmpty ? 'opacity-40' : ''}`}
          >
            {/* EN-TÊTE CATÉGORIE — blue-700, couleur primaire charte */}
            <button
              onClick={() => !isEmpty && toggleCategory(category.value)}
              disabled={isEmpty}
              className={`w-full flex items-center justify-between px-4 py-3.5 text-left bg-blue-900 text-white ${
                isEmpty ? 'cursor-not-allowed' : 'hover:bg-blue-950 cursor-pointer'
              } transition-colors`}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <svg
                  className={`w-4 h-4 opacity-70 transition-transform duration-200 shrink-0 ${isOpen ? 'rotate-90' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>

                {/* Icône Lucide — seule différenciation entre catégories */}
                <Icon className="w-5 h-5 shrink-0 opacity-90" />

                <span className="font-semibold text-base">{category.label}</span>

                {/* Aperçu types (catégorie fermée) */}
                {!isOpen && count > 0 && (
                  <div className="hidden sm:flex items-center gap-1 flex-wrap ml-1">
                    {typeGroups.slice(0, 3).map(({ type, docs }) => (
                      <span key={type} className="px-2 py-0.5 rounded text-xs font-medium bg-white/20 text-white">
                        {getTypeDocumentLabel(type)}{docs.length > 1 ? ` ×${docs.length}` : ''}
                      </span>
                    ))}
                    {typeGroups.length > 3 && (
                      <span className="text-xs opacity-60 font-medium">+{typeGroups.length - 3} types</span>
                    )}
                  </div>
                )}
              </div>

              <span className="ml-3 px-2.5 py-0.5 rounded-full text-sm font-bold shrink-0 bg-white/20 text-white">
                {count}
              </span>
            </button>

            {/* SOUS-GROUPES TYPE */}
            {isOpen && count > 0 && (
              <div>
                {typeGroups.map(({ type, docs }) => (
                  <TypeSubGroup
                    key={type}
                    type={type}
                    docs={docs}
                    basePath={basePath}
                    onArchive={onArchive}
                    archivageEnCours={archivageEnCours}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Groupe de rattrapage — documents sans catégorie valide */}
      {documentsNonClasses.length > 0 && (
        <div className="border border-orange-200 rounded-xl overflow-hidden shadow-sm">
          <button
            onClick={() => toggleCategory('non_classe' as CategorieDocument)}
            className="w-full flex items-center justify-between px-4 py-3.5 text-left bg-orange-700 text-white hover:bg-orange-800 cursor-pointer transition-colors"
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <svg
                className={`w-4 h-4 opacity-70 transition-transform duration-200 shrink-0 ${openCategories.has('non_classe' as CategorieDocument) ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <PackageOpen className="w-5 h-5 shrink-0 opacity-90" />
              <span className="font-semibold text-base">Non classés</span>
              <span className="text-xs opacity-75 ml-1">— catégorie manquante ou inconnue</span>
            </div>
            <span className="ml-3 px-2.5 py-0.5 rounded-full text-sm font-bold shrink-0 bg-white/20 text-white">
              {documentsNonClasses.length}
            </span>
          </button>
          {openCategories.has('non_classe' as CategorieDocument) && (
            <div>
              {(() => {
                const typeGroups = groupByType(documentsNonClasses);
                return typeGroups.map(({ type, docs }) => (
                  <TypeSubGroup
                    key={type}
                    type={type}
                    docs={docs}
                    basePath={basePath}
                    onArchive={onArchive}
                    archivageEnCours={archivageEnCours}
                  />
                ));
              })()}
            </div>
          )}
        </div>
      )}

      {!hasDocuments && (
        <div className="text-center py-10 text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
          </svg>
          <p className="font-medium">Aucun document dans la GED</p>
          <p className="text-sm mt-1">Utilisez SENTINELLE pour déposer vos premiers documents</p>
        </div>
      )}
    </div>
  );
}
